import { useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, Dialog, IconButton } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import { find, map, get, size, chain, slice } from "lodash"
import Form from "components/material/form"
import Tooltip from "components/material/tooltip"
import TablePagination from "components/material/table-pagination"
import { FIELD, ANALYTICS_TRACKING } from "helpers/shapes"
import Toolbar from "./toolbar"
import DeleteDialog from "./delete-dialog"
import useSort from "./hooks/sort"
import useRows from "./hooks/rows"
import useRowSelector from "./hooks/row-selector"
import useDeleter from "./hooks/deleter"
import useActionPermissions from "./hooks/action-permissions"
import TableMenu from "./menu"
import SortLabelWithTooltip from "components/sort-label-with-tooltip"

import "./styles.scss"

export default function OPTable({
  title,
  data,
  columns,
  idField,
  addForm,
  editForm,
  deleteForm,
  additionalOptions,
  addDisabledText,
  showToolbar = true,
  allowAbsoluteSort = false,
  filters,
  filterForm,
  actions,
  pagination,
  rowsPerPageOptions = [],
  rowsPerPage,
  className,
  favoritesScreen,
}) {
  const [dialog, setDialog] = useState(null)
  const [menu, setMenu] = useState(null)
  const [editedId, setEditedId] = useState(null)
  const [page, setPage] = useState(0)

  const [sortByColumn, direction, absolute, sortClass, sortValueGetter, dispatchSort] = useSort(columns, allowAbsoluteSort)
  const rows = useRows(columns, data, idField, sortValueGetter, direction, editForm, deleteForm, filters, favoritesScreen)
  const [selectedRows, rowCount, numSelected, dispatchSelect] = useRowSelector(rows)
  const [deletingItems, startDelete] = useDeleter(data, idField, selectedRows, setDialog)
  const [noActions, noEdit, noDelete] = useActionPermissions(rows, editForm, deleteForm)

  return <div className={classNames("components_table", className, { "has-pagination": pagination && size(rows) > rowsPerPage })}>
    {showToolbar && <Toolbar
      addDisabled={addForm === null}
      addDisabledText={addDisabledText}
      addHidden={addForm === false}
      title={title}
      numSelected={numSelected}
      onClickDelete={startDelete}
      onClickAdd={() => setDialog("add")}
      actions={actions}
      filters={filters}
      filterForm={filterForm}
    />}
    <TableContainer className="table-container">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {(!noActions && !noDelete) // Remove the cell if the delete action is not possible
              && <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={(_, checked) => { dispatchSelect({ type: "all", checked }) }}
                />
              </TableCell>}
            {columns.map((column, index) => (
              <TableCell
                className={column.id}
                key={column.id}
                align={column.numeric ? "right" : "left"}
                sortDirection={sortByColumn === index ? direction : false}
              >
                <SortLabelWithTooltip
                  noSortLabels={sortByColumn !== index}
                  alphabetical={!column.numeric}
                  absolute={absolute}
                  className={sortByColumn === index ? sortClass : ""}
                  active={sortByColumn === index}
                  direction={sortByColumn === index ? direction : "asc"}
                  onClick={() => dispatchSort({ index })}
                >
                  {column.name}
                </SortLabelWithTooltip>
              </TableCell>
            ))}
            {(!noActions && !noEdit) // Remove the cell if the edit action is not possible
              && <TableCell width="10%" align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {chain(rows)
            .thru(rows => pagination && rowsPerPage ? slice(rows, page * rowsPerPage, (page * rowsPerPage) + rowsPerPage) : rows)
            .map(row => {
              const isItemSelected = !!selectedRows[row.key]
              const { editable, deletable } = row

              return <TableRow key={row.key} data-id={row.key} hover selected={isItemSelected}>
                {!noActions && !noDelete // Remove the cell if the delete action is not possible
                  && <TableCell padding="checkbox">
                    {deletable // Remove only the checkbox if this row cannot be deleted
                      && <Checkbox
                        className={`check-${row.key}`}
                        checked={isItemSelected}
                        onChange={(_, checked) => { dispatchSelect({ type: "single", id: row.key, checked }) }}
                      />}
                  </TableCell>}
                {map(row.columns, (column, columnIndex) => {
                  return <TableCell key={columnIndex} className={columns[columnIndex].id}><span>{column}</span></TableCell>
                })}
                {!noActions && !noEdit // Remove the cell if the edit action is not possible
                  && <TableCell width="10%" align="right">
                    {editable // Remove only the menu if this row cannot be edited
                      && <Tooltip title="Modify">
                        <IconButton
                          data-id={`${row.key}-actions`}
                          onClick={(event) => {
                            setMenu({
                              anchor: event.currentTarget,
                              id: row.key,
                              item: find(data, datum => datum[idField] === row.key),
                            })
                          }}
                          size="large"
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>}
                  </TableCell>}
              </TableRow>
            })
            .value()}
        </TableBody>
      </Table>
    </TableContainer>
    {size(rows) > (rowsPerPage ?? 0) && pagination
      && <TablePagination
        component="div"
        className="outside-pagination"
        rowsPerPageOptions={rowsPerPageOptions}
        count={size(rows)}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(pageNumber) => setPage(pageNumber)}
      />}
    <Dialog open={!!dialog} className={`components_table-dialog-${dialog}`} PaperProps={{ style: { overflowY: "visible" } }} onClose={() => setDialog(null)}>
      {dialog === "add" && <Form {...addForm} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <DeleteDialog
          {...deleteForm}
          items={deletingItems}
          onClose={() => setDialog(null)}
          afterDelete={() => {
            setDialog(null)
            dispatchSelect({ type: "all", checked: false })
          }}
        />
      )}
      {dialog === "edit" && <Form {...editForm} editedItem={find(data, d => get(d, idField) === editedId)} additionalValues={{ id: editedId }} onClose={() => setDialog(null)} />}
    </Dialog>
    { menu && <TableMenu
      {...menu}
      title={title}
      editIsEnabled={get(editForm, "isEnabled")}
      deleteIsEnabled={get(deleteForm, "isEnabled")}
      additionalOptions={additionalOptions}
      onClose={() => setMenu(null)}
      setEditedId={setEditedId}
      setDialog={setDialog}
      startDelete={startDelete}
    />}
  </div>
}

OPTable.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    formatter: PropTypes.func,
    linkTo: PropTypes.func,
  })).isRequired,
  idField: PropTypes.string.isRequired,
  addForm: PropTypes.oneOfType([PropTypes.shape({
    title: PropTypes.string.isRequired,
    submitText: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(FIELD).isRequired,
    track: ANALYTICS_TRACKING,
  }), PropTypes.bool]),
  editForm: PropTypes.shape({
    title: PropTypes.string.isRequired,
    submitText: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isEnabled: PropTypes.func,
    getInitialValues: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(FIELD).isRequired,
    track: ANALYTICS_TRACKING,
  }),
  deleteForm: PropTypes.shape({
    type: PropTypes.string.isRequired,
    isEnabled: PropTypes.func,
    renderItem: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    track: ANALYTICS_TRACKING,
  }),
  additionalOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    displayFor: PropTypes.func,
    onClick: PropTypes.func.isRequired,
  })),
  addDisabledText: PropTypes.string,
  showToolbar: PropTypes.bool,
  allowAbsoluteSort: PropTypes.bool,
  filters: PropTypes.arrayOf(PropTypes.func),
  filterForm: PropTypes.node,
  actions: PropTypes.node,
  pagination: PropTypes.bool,
  rowsPerPageOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  rowsPerPage: PropTypes.number,
  className: PropTypes.string,
  favoritesScreen: PropTypes.bool,
}
